import { ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { MateCard } from "@/types/mateTypes";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { displayRelativeTime } from "@/lib/dateUtils";

interface MateCardItemProps {
  mate: MateCard;
}

export function MateCardItem({ mate }: MateCardItemProps) {
  const navigate = useNavigate();

  const handleAvatarClick = () => {
    // Navigate to mate's profile/workouts page
    // This would integrate with existing workout viewing functionality
    navigate(`/mates/${mate.mateship_id}`);
  };

  const getLastWorkoutText = () => {
    if (!mate.mate_user.last_trained_at) {
      return "No workouts yet";
    }
    return (
      "Last workout: " + displayRelativeTime(mate.mate_user.last_trained_at)
    );
  };

  const getInitials = () => {
    return mate.mate_user.display_name
      .split(" ")
      .map((name: string) => name[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <button
      onClick={handleAvatarClick}
      className="w-full flex items-center gap-3 p-3 bg-white hover:bg-gray-50 rounded-lg border border-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 relative"
      aria-label={`Open ${mate.mate_user.display_name}'s workouts`}
    >
      {/* Avatar with unread badge */}
      <div className="relative flex-shrink-0">
        <Avatar className="h-12 w-12 border-2 border-gray-200">
          <AvatarImage
            src={mate.mate_user.avatar_url || undefined}
            alt={`${mate.mate_user.display_name} avatar`}
            loading="lazy"
          />
          <AvatarFallback className="text-sm font-medium bg-gray-100 text-gray-600">
            {getInitials()}
          </AvatarFallback>
        </Avatar>

        {/* Unread badge */}
        {mate.has_unread && (
          <span
            className="absolute -right-1 -top-1 h-3 w-3 rounded-full bg-emerald-500 ring-2 ring-white animate-pulse"
            aria-hidden="true"
          />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 text-left">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 text-sm truncate">
            {mate.mate_user.display_name}
          </h3>
          {mate.has_unread && <span className="sr-only">New workouts</span>}
        </div>
        <p className="text-xs text-gray-500 mt-0.5 truncate">
          {getLastWorkoutText()}
        </p>
      </div>

      {/* Chevron Arrow */}
      <div className="flex-shrink-0">
        <ChevronRight className="h-5 w-5 text-gray-400" />
      </div>
    </button>
  );
}
